// src/components/GardenDrawingTool.tsx

/**
 * Interactive tool for drawing garden boundaries in the 'Suntap' app.
 * Provides UI controls for creating outdoor space polygons in 3D, allows
 * elevation adjustment, and handles garden naming. Manages the drawing state,
 * displays instructional dialogs, and provides visual feedback during the
 * drawing process. Converts between coordinate systems and handles user
 * interactions like undo, restart, and completing polygons. Can also be
 * triggered to edit an existing garden outline.
 */
import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Paper, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Checkbox, FormControlLabel } from '@mui/material';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GrassIcon from '@mui/icons-material/Grass';
import HeightIcon from '@mui/icons-material/Height';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { disposeObject3D, convertToExternalCoords, convertToInternalCoords, worldToLocal, convertFromExternalCoords } from '../../utils/threeUtils';
import { createPolygonVisualization } from '../../utils/gardenShapeUtils';
import { DrawingState, GardenBaseProps, Garden } from '../../types/gardenTypes'; // Added Garden type
import { 
    trackGardenDrawingCompleted, 
    trackPubGardenSubmission, 
    trackDrawingToolsPerformance 
} from '../../utils/analytics';

// Define names for the specific groups used during elevation editing
const ADJUSTED_ELEVATION_POLYGON_GROUP_NAME = 'AdjustedElevationPolygonGroup';

/**
 * Helper to clean garden names by removing pub name prefixes
 * Handles formats like "PubName - GardenName" or multiple prefixes
 */
const cleanGardenName = (fullName: string): string => {
    if (!fullName) return 'Garden';

    // If the name contains " - ", extract just the last part which should be the garden name
    if (fullName.includes(' - ')) {
        return fullName.split(' - ').pop() || fullName;
    }

    return fullName;
};

// Props specific to the drawing tool
export interface GardenDrawingToolProps extends GardenBaseProps {
    scene: THREE.Scene;
    container: HTMLElement;
    controls: OrbitControls;
    trueUpVector: THREE.Vector3;
    localOrigin: THREE.Vector3;
    onPolygonComplete?: (
      coordinates: [number, number, number][],
      gardenName?: string,
      isUncertain?: boolean
    ) => void;
    preservePolygon?: boolean;
    handleGardenNaming?: boolean;
    showInitialHelp?: boolean;
    googlePlaceId?: string;
    onEnterPlanView?: () => void;
    onExitPlanView?: () => void;
    onCancel?: () => void;
    onFocusOnPoint?: (point: THREE.Vector3) => void; // <-- Added property
  }
  
// Define the interface for the methods exposed via the ref
export interface GardenDrawingToolRef {
    handleCanvasClick: (event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => void;
    startEditMode: (gardenToEdit: Garden) => void; // Method to initiate editing
    startHeightAdjustment: (gardenToAdjust: Garden) => void; // Method to initiate height adjustment
    startDrawing: () => void; // Method to start drawing a new garden
    isBusy: () => boolean; // Method to check if the tool is in a state preventing interaction
    clearVisuals: () => void; // Method to clear drawing visuals
  }

// Use forwardRef to allow parent components to get a ref to this component
const GardenDrawingTool = forwardRef<GardenDrawingToolRef, GardenDrawingToolProps>(({
  scene,
  container,
  controls,
  trueUpVector,
  localOrigin,
  onPolygonComplete,
  preservePolygon = false,
  handleGardenNaming = false,
  showInitialHelp = true,
  googlePlaceId,
  onFocusOnPoint,
  onEnterPlanView,
  onExitPlanView,
  onCancel,
}, ref) => {

  // Ref for the THREE.Group that holds all drawing visuals (points, lines, fill)
  const drawingGroupRef = useRef<THREE.Group | null>(null);
  // Ref for the *initial* elevation point clicked, defines the drawing plane height
  const localOriginRef = useRef<THREE.Vector3 | null>(null); // Used for stable local coords if needed
  // Ref to track drawing start time for analytics
  const drawingStartTimeRef = useRef<number | null>(null);

  // Track mouse/drag state
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const mouseDownPosRef = useRef<{x: number, y: number} | null>(null);
  const dragThreshold = 3; // Pixels of movement to consider as a drag
  const lastCameraMoveTimeRef = useRef<number>(0); // Track time of last camera move

  // Drawing state
  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
    isSettingElevation: false,
    polygonPoints: [], // Stores WORLD coordinates of vertices during drawing OR original points during elevation edit
    elevationPoint: null, // The initial world point clicked to set elevation
    drawingPlane: null, // The THREE.Plane used for projecting clicks
    drawingElevation: null, // Not strictly needed if using plane
    drawingPrompt: '',
    isEditingElevation: false,
    elevationAdjustment: 0,
    savedPolygonCoordinates: [], // Stores final ECEF coordinates
  });

  const [baseElevationStep] = useState<number>(0.1); // Base step size in meters

  // UI Dialog State
  const [showWelcomeDialog, setShowWelcomeDialog] = useState<boolean>(showInitialHelp);
  const [showDrawingDialog, setShowDrawingDialog] = useState<boolean>(false);
  const [closePolygonDialog, setClosePolygonDialog] = useState<boolean>(false); // Correct state name
  const [showStartNewDrawingDialog, setShowStartNewDrawingDialog] = useState<boolean>(false);
  const [showElevationDialog, setShowElevationDialog] = useState<boolean>(false);
  const [showGardenNameDialog, setShowGardenNameDialog] = useState<boolean>(false);
  const [showEditGardenDialog, setShowEditGardenDialog] = useState<boolean>(false); // *** New state for edit modal ***

  // Garden Naming State
  const [gardenName, setGardenName] = useState<string>('');
  const [customGardenName, setCustomGardenName] = useState<string>('');
  const [showCustomNameField, setShowCustomNameField] = useState<boolean>(false);

  // Uncertainty flag
  const [isUncertain, setIsUncertain] = useState<boolean>(false);

  // Edit Existing State
  const [isEditingExisting, setIsEditingExisting] = useState<boolean>(false); // *** New state for edit mode ***
  const [gardenBeingEditedName, setGardenBeingEditedName] = useState<string>(''); // *** Store name for edit prompt ***
  const [gardenBeingEditedUid, setGardenBeingEditedUid] = useState<string>(''); // Store UID for elevation adjustment

  // Ref for the callback to avoid stale closures
  const onPolygonCompleteRef = useRef(onPolygonComplete);
  useEffect(() => {
    onPolygonCompleteRef.current = onPolygonComplete;
  }, [onPolygonComplete]);

  // Track drawing tool performance for analytics
  useEffect(() => {
    // Measure initialization time
    const startTime = performance.now();
    
    // Wait for essential components to be ready
    const checkReadiness = () => {
      if (drawingGroupRef.current && container) {
        const initTime = performance.now() - startTime;
        
        // Calculate approximate mesh count
        const meshCount = scene ? scene.children.filter(child => 
          child.type === 'Mesh' || 
          (child.isObject3D && child.children.some(c => c.type === 'Mesh'))).length : 0;
        
        // Send analytics data
        trackDrawingToolsPerformance(initTime, meshCount);
      }
    };
    
    // Check after a short delay to ensure components are mounted
    const timer = setTimeout(checkReadiness, 200);
    return () => clearTimeout(timer);
  }, [container, scene]);

  // --- Mouse tracking handlers ---
  const handleMouseDown = useCallback((event: MouseEvent) => {
    if ((drawingState.isDrawing && !drawingState.isEditingElevation) || drawingState.isEditingElevation) {
      mouseDownPosRef.current = { x: event.clientX, y: event.clientY };
      setIsDragging(false);
    }
  }, [drawingState.isDrawing, drawingState.isEditingElevation]);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (((drawingState.isDrawing && !drawingState.isEditingElevation) || drawingState.isEditingElevation) && mouseDownPosRef.current) {
      const dx = Math.abs(event.clientX - mouseDownPosRef.current.x);
      const dy = Math.abs(event.clientY - mouseDownPosRef.current.y);

      if (dx > dragThreshold || dy > dragThreshold) {
        setIsDragging(true);
        lastCameraMoveTimeRef.current = Date.now();
      }
    }
  }, [drawingState.isDrawing, drawingState.isEditingElevation]);

  const handleMouseUp = useCallback(() => {
    mouseDownPosRef.current = null;
  }, []);

  // --- Set up mouse event listeners ---
  useEffect(() => {
    if ((drawingState.isDrawing && !drawingState.isEditingElevation) || drawingState.isEditingElevation) {
      if (container) {
        container.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
          container.removeEventListener('mousedown', handleMouseDown);
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('mouseup', handleMouseUp);
        };
      }
    }
  }, [drawingState.isDrawing, drawingState.isEditingElevation, container, handleMouseDown, handleMouseMove, handleMouseUp]);

  // --- Initialise/Cleanup drawing group ---
  useEffect(() => {
    // Create drawing group ONCE and add to scene
    if (!drawingGroupRef.current) {
      drawingGroupRef.current = new THREE.Group();
      drawingGroupRef.current.name = 'GardenDrawingGroup';
      scene.add(drawingGroupRef.current);
    }

    const group = drawingGroupRef.current;
    return () => {
      if (group) {
        while (group.children.length > 0) {
            const child = group.children[0];
            disposeObject3D(child);
            // Ensure parent removal if dispose doesn't handle it completely
            if (child.parent) {
                child.parent.remove(child);
            }
        }
        if (group.parent) {
          group.parent.remove(group);
        }
      }
      drawingGroupRef.current = null;
    };
  }, [scene]);

  // --- Drawing Management ---

  // Clear ALL visuals within the main drawing group
  const clearDrawingVisuals = useCallback(() => {
    if (drawingGroupRef.current) {
        while (drawingGroupRef.current.children.length > 0) {
            const child = drawingGroupRef.current.children[0];
            disposeObject3D(child);
             // Ensure parent removal if dispose doesn't handle it completely
             if (child.parent) {
                child.parent.remove(child);
             }
        }
    }
  }, []);

  // Clear specific named visual group
  const clearSpecificVisual = useCallback((groupName: string) => {
    if (drawingGroupRef.current) {
        const objectToRemove = drawingGroupRef.current.getObjectByName(groupName);
        if (objectToRemove) {
            disposeObject3D(objectToRemove); // Use existing dispose utility
            // Ensure parent reference is removed if disposeObject3D doesn't handle it fully
            if (objectToRemove.parent) {
                 objectToRemove.parent.remove(objectToRemove);
            }
        }
    }
  }, []); // Stable as it only uses refs and imported utils


  // --- Visualisation Helpers ---

  // Wrapper around createPolygonVisualization utility to add the result to the drawing group
  const addPolygonVisualToGroup = useCallback((points: THREE.Vector3[], options: object) => {
    if (!drawingGroupRef.current || !container || !localOriginRef.current) {
        console.warn("Cannot add polygon visual: Missing requirements.", { hasGroup: !!drawingGroupRef.current, hasContainer: !!container, hasOrigin: !!localOriginRef.current });
        return null;
    }
    const { group } = createPolygonVisualization(
        points,
        container,
        localOriginRef.current,
        options
    );
    if (group) {
        drawingGroupRef.current.add(group);
    }
    return group;
  }, [container]); // Depends on container, refs are stable

  const createPolygonFill = useCallback((points: THREE.Vector3[], isElevationMode: boolean = false) => {
    if (!container || points.length < 3) {
        console.warn("Cannot create polygon fill: Missing requirements.", { hasContainer: !!container, points: points.length });
        return;
    }
    // Ensure local origin is set (important for consistency)
    if (!localOriginRef.current) {
         if (drawingState.elevationPoint) {
             localOriginRef.current = drawingState.elevationPoint.clone();
         } else if (points.length > 0) {
             localOriginRef.current = points[0].clone();
         } else {
             console.error("Cannot determine local origin for polygon fill.");
             return;
         }
    }

    // Clear existing drawing visuals *before* adding the new one
    // This function is typically called to show the *final* shape or the state before elevation editing
    clearDrawingVisuals();

    addPolygonVisualToGroup(
        points,
        {
            fillColor: 0x00aa00, // Green fill
            fillOpacity: 0.5,
            outlineColor: 0x00ff00, // Bright green outline
            outlineWidth: 2.5,
            vertexColor: 0xffff00, // Yellow vertices
            vertexSize: 0.25,
            showVertices: true,
            isElevationMode: isElevationMode, // Use provided flag
            customGroupName: 'ActiveDrawingPolygon' // Generic name for standard fill
        }
    );
}, [clearDrawingVisuals, container, drawingState.elevationPoint, addPolygonVisualToGroup]); // Added dependencies


  // Explicit cancellation function
  const cancelDrawing = useCallback(() => {
    const wasEditing = isEditingExisting; // Capture state before reset

    // If not preserving, OR if we were editing (cancel edit means discard changes)
    // clear the visuals managed by this tool. Existing orange gardens are untouched.
    if (!preservePolygon || wasEditing) {
        clearDrawingVisuals();
    } else if (preservePolygon && !wasEditing && drawingState.savedPolygonCoordinates.length > 0) {
        // Redraw preserved polygon ONLY if NOT editing and preservePolygon is true
         if(drawingState.savedPolygonCoordinates.length >= 3) {
            try {
                const worldPoints = drawingState.savedPolygonCoordinates.map(p => convertToInternalCoords(p));
                if (worldPoints.length > 0) localOriginRef.current = worldPoints[0].clone(); // Set origin for redraw
                createPolygonFill(worldPoints); // Redraw the final saved state
            } catch(e) {
                console.error("Error redrawing preserved polygon on cancel:", e);
                clearDrawingVisuals(); // Fallback
            }
        } else {
             clearDrawingVisuals(); // Clear if no saved coords to preserve
        }
    }

    setDrawingState(prev => ({
        ...prev,
        isDrawing: false,
        isSettingElevation: false,
        polygonPoints: [],
        elevationPoint: null,
        drawingPlane: null,
        drawingElevation: null,
        drawingPrompt: '',
        isEditingElevation: false, // Also cancel elevation editing if active
        elevationAdjustment: 0,
        // Keep saved coordinates ONLY if preserving AND NOT editing, otherwise clear
        savedPolygonCoordinates: (preservePolygon && !wasEditing && prev.savedPolygonCoordinates.length > 0) ? prev.savedPolygonCoordinates : [],
      }));
      localOriginRef.current = null;
      setIsEditingExisting(false); // Reset edit mode flag
      setGardenBeingEditedName(''); // Reset edited name
      setGardenBeingEditedUid(''); // Reset edited UID
      setIsUncertain(false); // Reset uncertainty flag
      
      // Reset drawing start time for analytics
      drawingStartTimeRef.current = null;
  
      // Close dialogs
      setShowDrawingDialog(false); // Hide instruction dialog
      setClosePolygonDialog(false); // Hide close confirmation dialog
      setShowElevationDialog(false); // Hide elevation dialog
      setShowEditGardenDialog(false); // Hide edit dialog
      // Don't hide naming dialog here, its own cancel handles that
  
      if (controls) {
        controls.enabled = true; // Re-enable controls
      }
  
      // <<< ADDED: Call the onCancel callback passed from parent
      onCancel?.();
  
    }, [preservePolygon, clearDrawingVisuals, controls, drawingState.savedPolygonCoordinates, createPolygonFill, isEditingExisting, onCancel]);


  const startNewDrawing = useCallback(() => {
    // Cancel any ongoing edit first
    if (isEditingExisting) {
        cancelDrawing(); // Reset edit state properly
    }
    clearDrawingVisuals();

    // Set drawing start time for analytics
    drawingStartTimeRef.current = Date.now();

    setDrawingState({
      isDrawing: true,
      isSettingElevation: true,
      polygonPoints: [],
      elevationPoint: null,
      drawingPlane: null,
      drawingElevation: null,
      drawingPrompt: 'First, tap on the floor of the outdoor space.',
      isEditingElevation: false,
      elevationAdjustment: 0,
      savedPolygonCoordinates: [], // Clear saved coords for new drawing
    });
    localOriginRef.current = null; // Reset local origin
    setIsEditingExisting(false); // Ensure edit mode is off
    setGardenBeingEditedName('');
    setGardenBeingEditedUid('');
    setIsUncertain(false); // Reset uncertainty flag

    setShowDrawingDialog(true); // Show instructions
    setShowGardenNameDialog(false);
    setShowStartNewDrawingDialog(false);
    setShowElevationDialog(false);
    setShowEditGardenDialog(false); // Hide edit dialog
    setShowWelcomeDialog(false);

    // Keep controls enabled during elevation point selection
  }, [clearDrawingVisuals, controls, isEditingExisting, cancelDrawing]); // Added dependencies


  const startDrawing = useCallback(() => {
    if (showGardenNameDialog || isEditingExisting || drawingState.isEditingElevation) return; // Don't allow if busy
    // If there are saved coordinates OR if the drawing group has children (visuals exist)
    if (drawingState.savedPolygonCoordinates.length > 0 || (drawingGroupRef.current && drawingGroupRef.current.children.length > 0)) {
      setShowStartNewDrawingDialog(true); // Ask to overwrite
    } else {
      startNewDrawing(); // Start fresh
    }
  }, [showGardenNameDialog, drawingState.savedPolygonCoordinates, drawingState.isEditingElevation, startNewDrawing, isEditingExisting]); // Added isEditingExisting, isEditingElevation


    const addPointVisual = useCallback((worldPoint: THREE.Vector3) => {
        if (!drawingGroupRef.current || !container) {
             console.warn("Cannot add point visual: Missing drawing group or container.");
             return;
        }

        // If localOrigin isn't set yet, set it to the elevation point (which should be set by now)
        if (!localOriginRef.current && drawingState.elevationPoint) {
            localOriginRef.current = drawingState.elevationPoint.clone();
        } else if (!localOriginRef.current) {
            // Fallback if elevation point isn't set somehow (should not happen in normal flow)
            localOriginRef.current = worldPoint.clone();
            console.warn("Local origin not set, using current point as origin.");
        }

        // Get or create a points group positioned at the local origin
        let pointsGroup = drawingGroupRef.current.getObjectByName('points_group') as THREE.Group;
        if (!pointsGroup) {
            pointsGroup = new THREE.Group();
            pointsGroup.name = 'points_group';
            // Position the group itself at the world origin, children will use local coords relative to localOriginRef
            if (localOriginRef.current) pointsGroup.position.copy(localOriginRef.current);
            drawingGroupRef.current.add(pointsGroup);
        }

        // Convert to local coordinates for numerical stability relative to the chosen origin
        const localPoint = worldToLocal(worldPoint, localOriginRef.current);

        // Simple red sphere for points during drawing
        const pointGeometry = new THREE.SphereGeometry(0.2, 16, 16);
        const pointMaterial = new THREE.MeshBasicMaterial({
            color: 0xff0000,
            depthTest: false, // Draw on top during process
            depthWrite: false,
            transparent: true,
        });
        const pointMesh = new THREE.Mesh(pointGeometry, pointMaterial);
        pointMesh.position.copy(localPoint); // Position relative to pointsGroup origin
        pointMesh.renderOrder = 20; // High render order during drawing
        pointsGroup.add(pointMesh);

        // Draw line from previous point if exists
        if (drawingState.polygonPoints.length > 0 && localOriginRef.current) { // Ensure origin exists for prev point conversion
            const prevWorldPoint = drawingState.polygonPoints[drawingState.polygonPoints.length - 1];
            const prevLocalPoint = worldToLocal(prevWorldPoint, localOriginRef.current);

            const positions = [
                prevLocalPoint.x, prevLocalPoint.y, prevLocalPoint.z,
                localPoint.x, localPoint.y, localPoint.z
            ];
            const lineGeometry = new LineGeometry();
            lineGeometry.setPositions(positions);
            const lineMaterial = new LineMaterial({
                color: 0x00ff00, // Green line during drawing
                linewidth: 2, // Adjusted line width
                resolution: new THREE.Vector2(container.clientWidth, container.clientHeight),
                dashed: false,
                depthTest: false, // Draw on top
                depthWrite: false,
                transparent: true,
                alphaToCoverage: true,
            });
            const line = new Line2(lineGeometry, lineMaterial);
            line.renderOrder = 20; // Same as points during drawing
            line.frustumCulled = false;
            pointsGroup.add(line); // Add line to the points group
        }
    }, [drawingState.polygonPoints, drawingState.elevationPoint, container]); // Added dependencies


    // --- Drawing Actions ---
    const finishDrawing = useCallback(() => {
        if (!drawingState.isDrawing || drawingState.polygonPoints.length < 3) {
            setDrawingState(prev => ({ ...prev, drawingPrompt: 'Please add at least 3 points to complete the outline.' }));
            setShowDrawingDialog(true); // Re-show dialog with error
            return;
        }

        let finalPoints = [...drawingState.polygonPoints];
        if (finalPoints.length > 0 && !finalPoints[0].equals(finalPoints[finalPoints.length - 1])) {
            finalPoints.push(finalPoints[0].clone());
        }
        
        // Track garden drawing completion via analytics
        if (drawingStartTimeRef.current) {
            const drawingDuration = (Date.now() - drawingStartTimeRef.current) / 1000; // Convert to seconds
            trackGardenDrawingCompleted(
                googlePlaceId || '', 
                finalPoints.length, 
                isEditingExisting, 
                drawingDuration
            );
        }
        
        // --- CORE FIX: Convert local points to absolute world points ---
        const absolutePoints = finalPoints.map(p => p.clone().add(localOrigin));
        const ecefCoordinates: [number, number, number][] = absolutePoints.map(p => convertToExternalCoords(p));

        // Create the final visualization (will clear previous temp visuals)
        // Note: createPolygonFill works with local points, which is correct for display
        createPolygonFill(finalPoints);

        // Store state before reset
        const wasEditing = isEditingExisting;
        const editedGardenUid = gardenBeingEditedUid;
        const finalUncertainty = isUncertain;

        // Reset state
        setDrawingState(prev => ({
            ...prev,
            savedPolygonCoordinates: ecefCoordinates,
            isDrawing: false,
            isSettingElevation: false,
            drawingPrompt: '',
            polygonPoints: [],
            elevationPoint: null,
            drawingPlane: null,
            drawingElevation: null,
        }));
        // localOriginRef might be needed if preserving, keep it for now
        // Don't reset uncertainty here, do it after callback/dialog

        // Focus camera
        if (onFocusOnPoint && finalPoints.length > 0) {
            const center = new THREE.Vector3();
            finalPoints.slice(0, -1).forEach(point => center.add(point)); // Exclude duplicate end point for center calc
            center.divideScalar(finalPoints.length -1);
            onFocusOnPoint(center);
        }

        setShowDrawingDialog(false);
        setClosePolygonDialog(false);
        setShowEditGardenDialog(false);

        if (wasEditing && editedGardenUid) {
            setTempSavedData({ ecefCoordinates, finalPoints });
            setShowConfirmEditDialog(true);
            return; // Exit early
        }

        if (handleGardenNaming && !wasEditing) {
            setShowGardenNameDialog(true);
            // Controls remain disabled
        } else {
            if (onPolygonCompleteRef.current) {
                onPolygonCompleteRef.current(ecefCoordinates, undefined, finalUncertainty); // Pass NEW coordinates, no name yet for new
            }
            setIsUncertain(false); // Reset uncertainty after use
            if (controls) {
                controls.enabled = true;
            }
        }
    }, [ drawingState.isDrawing, drawingState.polygonPoints, createPolygonFill, handleGardenNaming, onFocusOnPoint, onExitPlanView, controls, isEditingExisting, gardenBeingEditedUid, gardenBeingEditedName, isUncertain, clearDrawingVisuals, googlePlaceId, localOrigin ]); // Add localOrigin to dependencies


    // --- Confirm Edit Dialog ---
    const [showConfirmEditDialog, setShowConfirmEditDialog] = useState<boolean>(false);
    const [tempSavedData, setTempSavedData] = useState<{
        ecefCoordinates: [number, number, number][];
        finalPoints: THREE.Vector3[];
    } | null>(null);

    const handleConfirmEdit = useCallback(() => {
        setShowConfirmEditDialog(false);
        if (!tempSavedData || !gardenBeingEditedUid || !gardenBeingEditedName) {
            console.error("Missing data for garden edit confirmation");
            // Attempt cleanup anyway
             clearDrawingVisuals();
             setIsEditingExisting(false);
             setGardenBeingEditedName('');
             setGardenBeingEditedUid('');
             setTempSavedData(null);
             setIsUncertain(false);
             if (controls) controls.enabled = true;
            return;
        }

        if (onPolygonCompleteRef.current) {
            const nameWithUid = `${gardenBeingEditedName}::${gardenBeingEditedUid}`;
            onPolygonCompleteRef.current(tempSavedData.ecefCoordinates, nameWithUid, isUncertain); // Pass uncertainty state captured during drawing
        }

        // Clear drawing visuals (the green one) as GardenVisualisation will take over
        clearDrawingVisuals();
        setDrawingState(prev => ({ ...prev, savedPolygonCoordinates: [] })); // Clear saved coords from tool state

        // Reset edit state
        setIsEditingExisting(false);
        setGardenBeingEditedName('');
        setGardenBeingEditedUid('');
        setTempSavedData(null);
        setIsUncertain(false); // Reset uncertainty after use

        if (controls) controls.enabled = true;

    }, [tempSavedData, gardenBeingEditedName, gardenBeingEditedUid, onPolygonCompleteRef, clearDrawingVisuals, controls, isUncertain]); // Added isUncertain

    const restartDrawing = useCallback(() => {
        if (drawingState.isSettingElevation || !drawingState.elevationPoint || !drawingState.drawingPlane || !drawingGroupRef.current || isEditingExisting) {
            console.warn("Cannot restart outline drawing at this stage.");
            return;
        }

        // Remove only the points/lines added during outline tracing
        const pointsGroup = drawingGroupRef.current.getObjectByName('points_group');
        if (pointsGroup) {
             // Remove group and its children
             disposeObject3D(pointsGroup);
             if(pointsGroup.parent) pointsGroup.parent.remove(pointsGroup);
        }

        setDrawingState(prev => ({
            ...prev,
            polygonPoints: [], // Clear only the outline points
            drawingPrompt: 'Start tracing the outline again from the first point.',
        }));
        setShowDrawingDialog(true); // Ensure prompt is visible
    }, [ drawingState.isSettingElevation, drawingState.elevationPoint, drawingState.drawingPlane, isEditingExisting ]); // Added isEditingExisting

    const undoLastPoint = useCallback(() => {
        if (drawingState.polygonPoints.length === 0 || drawingState.isSettingElevation || !drawingGroupRef.current) return;

        const newPolygonPoints = drawingState.polygonPoints.slice(0, -1);

        const pointsGroup = drawingGroupRef.current.getObjectByName('points_group') as THREE.Group;
        if (pointsGroup && pointsGroup.children.length > 0) {
            // Remove last TWO children (line and point) if they exist
            if (pointsGroup.children.length >= 2 && drawingState.polygonPoints.length > 1) {
                disposeObject3D(pointsGroup.children[pointsGroup.children.length - 1]); // Line
                disposeObject3D(pointsGroup.children[pointsGroup.children.length - 2]); // Point
            } else if (pointsGroup.children.length >= 1) {
                 disposeObject3D(pointsGroup.children[pointsGroup.children.length - 1]); // Just the first point
            }
        }

        setDrawingState(prev => ({ ...prev, polygonPoints: newPolygonPoints }));
    }, [drawingState.polygonPoints, drawingState.isSettingElevation]);


    // --- Canvas Click Logic ---
    const handleCanvasClick = useCallback((event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => {
        if (!drawingState.isDrawing || drawingState.isEditingElevation) return;
        if (closePolygonDialog || showStartNewDrawingDialog || showGardenNameDialog || showEditGardenDialog) return;

        const timeSinceLastCameraMove = Date.now() - lastCameraMoveTimeRef.current;
        if (isDragging || timeSinceLastCameraMove < 150) return;

        if (drawingState.isSettingElevation) {
            if (intersection) {
                const elevationWorldPoint = intersection.point.clone();
                localOriginRef.current = elevationWorldPoint.clone(); // Set origin for this drawing session
                const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(trueUpVector, elevationWorldPoint);

                setDrawingState(prev => ({
                    ...prev,
                    elevationPoint: elevationWorldPoint,
                    isSettingElevation: false,
                    drawingPlane: plane,
                    drawingPrompt: isEditingExisting
                       ? `Elevation set for '${gardenBeingEditedName}'. Now draw the NEW outline with short taps. Tap near start to finish.`
                       : 'Elevation set. Now draw the outline with short taps. Tap near the first point to finish.',
                }));

                clearDrawingVisuals(); // Clear previous visuals
                if (drawingGroupRef.current && localOriginRef.current) {
                    const elevMarkerGroup = new THREE.Group();
                    elevMarkerGroup.name = 'elevation_marker';
                    elevMarkerGroup.position.copy(localOriginRef.current);
                    drawingGroupRef.current.add(elevMarkerGroup);

                    const pointGeometry = new THREE.SphereGeometry(0.3, 16, 16);
                    const pointMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff, depthTest: false, transparent: true });
                    const pointMesh = new THREE.Mesh(pointGeometry, pointMaterial);
                    pointMesh.position.set(0, 0, 0);
                    pointMesh.renderOrder = 10;
                    elevMarkerGroup.add(pointMesh);

                    const arrowHelper = new THREE.ArrowHelper(trueUpVector, new THREE.Vector3(0, 0, 0), 1, 0x0000ff, 0.2, 0.1);
                    arrowHelper.renderOrder = 10;
                    elevMarkerGroup.add(arrowHelper);
                }
                setShowDrawingDialog(true);
            } else {
                console.warn("Tap missed. Please tap directly on the ground surface.");
                setDrawingState(prev => ({ ...prev, drawingPrompt: 'Tap missed. Please tap directly on the ground surface.' }));
                setShowDrawingDialog(true);
            }
        } else { // Adding Polygon Outline Points
            if (drawingState.drawingPlane) {
                const target = new THREE.Vector3();
                if (raycaster.ray.intersectPlane(drawingState.drawingPlane, target)) {
                    const worldPoint = target.clone();

                    if (drawingState.polygonPoints.length >= 2) {
                        const firstPoint = drawingState.polygonPoints[0];
                        const distanceToFirst = worldPoint.distanceTo(firstPoint);
                        const closeThreshold = 1.0;
                        if (distanceToFirst < closeThreshold) {
                            setClosePolygonDialog(true);
                            return;
                        }
                    }
                    addPointVisual(worldPoint);
                    setDrawingState(prev => ({ ...prev, polygonPoints: [...prev.polygonPoints, worldPoint] }));
                } else {
                    console.warn("Ray did not intersect drawing plane.");
                }
            } else {
                console.error("Drawing plane not set.");
            }
        }
    }, [
        drawingState.isDrawing, drawingState.isSettingElevation, drawingState.isEditingElevation,
        drawingState.drawingPlane, drawingState.polygonPoints,
        addPointVisual, clearDrawingVisuals, trueUpVector,
        closePolygonDialog, showStartNewDrawingDialog, showGardenNameDialog, showEditGardenDialog,
        controls, isDragging, isEditingExisting, gardenBeingEditedName,
    ]);


    // --- Edit Existing Garden Mode ---
    const startEditMode = useCallback((gardenToEdit: Garden) => {
        clearDrawingVisuals();
        
        // Set drawing start time for analytics
        drawingStartTimeRef.current = Date.now();
        
        setDrawingState({
            isDrawing: true,
            isSettingElevation: true,
            polygonPoints: [],
            elevationPoint: null,
            drawingPlane: null,
            drawingElevation: null,
            drawingPrompt: `Editing '${cleanGardenName(gardenToEdit.name || gardenToEdit.uid)}'. Click floor for the NEW garden's elevation.`,
            isEditingElevation: false,
            elevationAdjustment: 0,
            savedPolygonCoordinates: [],
        });
        localOriginRef.current = null;

        setIsEditingExisting(true);
        setGardenBeingEditedName(cleanGardenName(gardenToEdit.name || gardenToEdit.uid));
        setGardenBeingEditedUid(gardenToEdit.uid || '');
        setIsUncertain(gardenToEdit.is_uncertain || false);

        setShowDrawingDialog(false);
        setClosePolygonDialog(false);
        setShowStartNewDrawingDialog(false);
        setShowElevationDialog(false);
        setShowGardenNameDialog(false);
        setShowWelcomeDialog(false);
        setShowEditGardenDialog(true); // Show edit instructions

        if (controls) controls.enabled = true;

    }, [clearDrawingVisuals, controls]);

    // --- Height Adjustment for Existing Garden Mode ---
    const startHeightAdjustment = useCallback((gardenToAdjust: Garden) => {
        if (drawingState.isDrawing || drawingState.isEditingElevation || isEditingExisting || showGardenNameDialog) {
            console.warn("Cannot start height adjustment due to current state.");
            return;
        }

        try {
            if (!gardenToAdjust.garden_latlon || gardenToAdjust.garden_latlon.length < 3) {
                console.warn(`Garden ${gardenToAdjust.name || gardenToAdjust.uid} has insufficient coordinates.`);
                return;
            }

            // Set drawing start time for analytics
            drawingStartTimeRef.current = Date.now();

            // Convert lat/lon/alt to ECEF THREE.Vector3
            const ecefPoints = gardenToAdjust.garden_latlon.map(coords => {
                const alt = gardenToAdjust.elevation ?? 0;
                const ecefVec = convertFromExternalCoords([coords[0], coords[1], alt]);
                return ecefVec;
            });

            // Convert ECEF Vector3 to internal World Vector3
            const worldPoints = ecefPoints.map(p => convertToInternalCoords([p.x, p.y, p.z]));

            if (worldPoints.length < 3) {
                console.error("Insufficient points for height adjustment after conversion.");
                return;
            }

            setGardenBeingEditedName(cleanGardenName(gardenToAdjust.name || gardenToAdjust.uid));
            setGardenBeingEditedUid(gardenToAdjust.uid || '');
            setIsUncertain(gardenToAdjust.is_uncertain || false);
            localOriginRef.current = worldPoints[0].clone();

            const originalEcefCoords = worldPoints.map(p => convertToExternalCoords(p));

            setDrawingState(prev => ({
                ...prev,
                polygonPoints: worldPoints, // Store ORIGINAL world points
                isEditingElevation: true,
                elevationAdjustment: 0,
                elevationPoint: worldPoints[0].clone(),
                savedPolygonCoordinates: originalEcefCoords, // Store ORIGINAL ECEF
            }));

            clearDrawingVisuals(); // Clear any previous drawings first

            setShowElevationDialog(true);
            // Keep controls enabled
        } catch (e) {
            console.error("Error preparing garden for height adjustment:", e);
            cancelDrawing(); // Reset state on error
        }
    // Updated dependencies if needed, `cancelDrawing` includes `onCancel` now
    }, [drawingState.isDrawing, drawingState.isEditingElevation, isEditingExisting, showGardenNameDialog, container, clearDrawingVisuals, addPolygonVisualToGroup, cancelDrawing]);


    // --- Check if tool is busy ---
    const isBusy = useCallback(() => {
        return drawingState.isDrawing ||
               drawingState.isEditingElevation ||
               showGardenNameDialog ||
               showEditGardenDialog ||
               showElevationDialog ||
               closePolygonDialog ||
               showStartNewDrawingDialog;
    }, [
        drawingState.isDrawing,
        drawingState.isEditingElevation,
        showGardenNameDialog,
        showEditGardenDialog,
        showElevationDialog,
        closePolygonDialog,
        showStartNewDrawingDialog
    ]);

    // --- Expose methods via ref ---
    useImperativeHandle(ref, () => ({
        handleCanvasClick: handleCanvasClick,
        startEditMode: startEditMode,
        startHeightAdjustment: startHeightAdjustment,
        startDrawing: startDrawing,
        isBusy: isBusy,
        clearVisuals: clearDrawingVisuals,
    }), [handleCanvasClick, startEditMode, startHeightAdjustment, startDrawing, isBusy, clearDrawingVisuals]);


    // --- Elevation Editing ---
    const dismissElevationDialog = useCallback(() => {
        setShowElevationDialog(false); // Just hide the dialog
    }, []);

    const cancelEditingElevation = useCallback(() => {
        if (!drawingState.isEditingElevation) return;
        clearDrawingVisuals(); // Clear ALL visuals (original outline + adjusted fill)

        // Reset state completely, discarding changes
        setDrawingState(prev => ({
            ...prev,
            isEditingElevation: false,
            elevationAdjustment: 0,
            polygonPoints: [],
            savedPolygonCoordinates: [], // Clear saved coords as we cancelled
        }));
        localOriginRef.current = null; // Reset origin

        setShowElevationDialog(false);
        setGardenBeingEditedName('');
        setGardenBeingEditedUid('');
        setIsUncertain(false); // Reset uncertainty
        
        // Reset drawing start time for analytics
        drawingStartTimeRef.current = null;

        if(controls) controls.enabled = true; // Re-enable controls

        onCancel?.();

    }, [drawingState.isEditingElevation, clearDrawingVisuals, controls, onCancel]);

    // Acceleration logic refs
    const accelerationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pressStartTimeRef = useRef<number | null>(null);
    const currentStepRef = useRef<number>(baseElevationStep);

    const clearAccelerationInterval = useCallback(() => {
      if (accelerationIntervalRef.current) {
        clearInterval(accelerationIntervalRef.current);
        accelerationIntervalRef.current = null;
      }
      pressStartTimeRef.current = null;
      currentStepRef.current = baseElevationStep; // Reset step size
    }, [baseElevationStep]);

    // Universal function to change elevation by a step
    const changeElevation = useCallback((step: number) => {
        setDrawingState(prev => ({ ...prev, elevationAdjustment: prev.elevationAdjustment + step }));
    }, []);

    const handleElevationButtonPress = useCallback((direction: 'up' | 'down') => {
      clearAccelerationInterval(); // Clear any existing interval

      const stepSign = direction === 'up' ? 1 : -1;

      // Initial immediate step
      changeElevation(stepSign * baseElevationStep);

      pressStartTimeRef.current = Date.now();
      currentStepRef.current = baseElevationStep; // Start with base step

      accelerationIntervalRef.current = setInterval(() => {
        if (!pressStartTimeRef.current) {
          clearAccelerationInterval();
          return;
        }

        const holdDuration = Date.now() - pressStartTimeRef.current;
        let dynamicStep = baseElevationStep;

        // Acceleration logic (increase step over time)
        if (holdDuration > 3500) { // Faster max acceleration
            dynamicStep = Math.min(baseElevationStep * 15, 1.5); // Cap at 1.5m step
        } else if (holdDuration > 2000) {
            dynamicStep = baseElevationStep * 8;
        } else if (holdDuration > 1000) {
            dynamicStep = baseElevationStep * 4;
        } else if (holdDuration > 600) {
             dynamicStep = baseElevationStep * 2;
        }
        // else use initial baseElevationStep

        changeElevation(stepSign * dynamicStep);

      }, 300); // Faster interval (e.g., 80ms) for smoother acceleration
    }, [clearAccelerationInterval, changeElevation, baseElevationStep]);

    // Cleanup interval on component unmount or state change
    useEffect(() => {
      // Clear interval if elevation editing stops
      if (!drawingState.isEditingElevation) {
        clearAccelerationInterval();
      }
      // Cleanup function for unmount
      return () => clearAccelerationInterval();
    }, [drawingState.isEditingElevation, clearAccelerationInterval]);


    // Effect to update polygon visualization in real-time during elevation adjustment
    // --- Elevation Adjustment Visualisation Effect ---
    // This effect now correctly draws *only* the green adjustable polygon
    useEffect(() => {
        // Only run if actively editing elevation and have *original* points to adjust
        if (drawingState.isEditingElevation && drawingState.polygonPoints.length >= 3) {

            // --- SELECTIVELY Clear previous ADJUSTED polygon ---
            clearSpecificVisual(ADJUSTED_ELEVATION_POLYGON_GROUP_NAME);

            const adjustmentVector = trueUpVector.clone().multiplyScalar(drawingState.elevationAdjustment);
            // Adjust copies of the *original* points stored in polygonPoints
            const adjustedVisualPoints = drawingState.polygonPoints.map(p => p.clone().add(adjustmentVector));

            // Ensure origin is set (should be set by startHeightAdjustment)
            if (!localOriginRef.current && drawingState.polygonPoints.length > 0) {
                localOriginRef.current = drawingState.polygonPoints[0].clone();
                console.warn("Re-setting local origin during elevation adjustment effect.");
            }

            // --- Draw the NEW adjusted polygon (filled) ---
            if (localOriginRef.current && container) {
                addPolygonVisualToGroup(
                    adjustedVisualPoints, // Use ADJUSTED points
                    {
                        fillColor: 0x00aa00, // Green fill for the moving one
                        fillOpacity: 0.6,
                        outlineColor: 0x00ff00,
                        outlineWidth: 2.5,
                        vertexColor: 0xffff00,
                        vertexSize: 0.25,
                        isElevationMode: true, // Ensure correct depth testing/writing
                        outlineOnly: false,    // <<< CONFIRMED: Draw filled polygon
                        showVertices: true,    // Show vertices on the moving one
                        customGroupName: ADJUSTED_ELEVATION_POLYGON_GROUP_NAME // Specific name
                    }
                );
            }
        }
        // Run whenever adjustment changes while editing, or when entering edit mode with points
    }, [drawingState.isEditingElevation, drawingState.elevationAdjustment, drawingState.polygonPoints, trueUpVector, container, clearSpecificVisual, addPolygonVisualToGroup]);


    const applyElevationAdjustment = useCallback(() => {
        if (!drawingState.isEditingElevation || drawingState.polygonPoints.length === 0 ) {
             // <<< Changed: Call cancelEditingElevation which now handles calling onCancel
             cancelEditingElevation();
             return;
        }
        const adjustment = drawingState.elevationAdjustment;
        if (adjustment === 0) {
            // <<< Changed: Call cancelEditingElevation which now handles calling onCancel
            cancelEditingElevation();
            return;
        }

        // Calculate final points based on original points + adjustment
        const adjustmentVector = trueUpVector.clone().multiplyScalar(adjustment);
        let updatedWorldPoints = drawingState.polygonPoints.map(p => p.clone().add(adjustmentVector));

        // Ensure closed loop for ECEF conversion
        if (updatedWorldPoints.length >= 1 && !updatedWorldPoints[0].equals(updatedWorldPoints[updatedWorldPoints.length - 1])) {
            updatedWorldPoints.push(updatedWorldPoints[0].clone());
        }

        // --- CORE FIX: Convert local points to absolute world points ---
        const absoluteUpdatedWorldPoints = updatedWorldPoints.map(p => p.clone().add(localOrigin));
        const updatedEcefCoordinates = absoluteUpdatedWorldPoints.map(p => convertToExternalCoords(p));

        // Capture details before state reset
        const editedGardenUid = gardenBeingEditedUid;
        const editedGardenName = gardenBeingEditedName;
        const finalUncertainty = isUncertain;

        // Reset state (clears polygonPoints used for editing)
        // Note: savedPolygonCoordinates is cleared later by onPolygonCompleteRef call context in parent
        setDrawingState(prev => ({
            ...prev,
            // savedPolygonCoordinates: updatedEcefCoordinates, // No need to save here, parent receives it
            polygonPoints: [],
            isEditingElevation: false,
            elevationAdjustment: 0,
        }));
        localOriginRef.current = null; // Reset origin after adjustment is done

        // Reset garden editing state
        setGardenBeingEditedName('');
        setGardenBeingEditedUid('');
        setIsUncertain(false); // Reset uncertainty flag
        
        // Reset drawing start time after analytics
        if (drawingStartTimeRef.current) {
            const adjustmentDuration = (Date.now() - drawingStartTimeRef.current) / 1000; // Convert to seconds
            trackGardenDrawingCompleted(
                googlePlaceId || '', 
                updatedWorldPoints.length, 
                true, // It's an edit operation
                adjustmentDuration
            );
            drawingStartTimeRef.current = null;
        }

        // --- Clear ALL elevation visuals ---
        // This will be handled by the parent via handlePolygonComplete calling clearVisuals
        // clearDrawingVisuals(); // <<< REMOVED: Let parent handle final visual clearing

        setShowElevationDialog(false);
        if(controls) controls.enabled = true;

        // Trigger callback (parent will clear visuals and reset gardenBeingAdjustedUid)
        if (onPolygonCompleteRef.current) {
            if (editedGardenUid) {
                const nameWithUid = `${editedGardenName}::${editedGardenUid}`;
                onPolygonCompleteRef.current(updatedEcefCoordinates, nameWithUid, finalUncertainty);
            } else {
                 console.warn("Applying elevation adjustment without an associated existing garden UID.");
                 onPolygonCompleteRef.current(updatedEcefCoordinates, undefined, finalUncertainty);
            }
        } else {
             // If no callback, manually clear visuals and call onCancel? Or assume parent handles it.
             // For now, assume parent's handlePolygonComplete is sufficient.
              console.warn("ApplyElevationAdjustment: onPolygonCompleteRef is not defined.");
              // Fallback clear visuals and notify cancellation state change if no primary callback.
              clearDrawingVisuals();
              onCancel?.(); // Notify parent that adjustment mode ended
        }


    }, [
        drawingState.isEditingElevation, drawingState.elevationAdjustment, drawingState.polygonPoints,
        cancelEditingElevation, // cancelEditingElevation now includes onCancel call
        trueUpVector, controls,
        gardenBeingEditedUid, gardenBeingEditedName, isUncertain, onPolygonCompleteRef,
        clearDrawingVisuals, // Keep clearDrawingVisuals dependency for fallback scenario
        onCancel, googlePlaceId, localOrigin // Add localOrigin to dependencies
    ]);

    // --- Garden Naming Handlers ---
    const handleGardenNameChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setGardenName(value);
        setShowCustomNameField(value === 'Custom');
        if (value !== 'Custom') setCustomGardenName('');
    };
    const handleCustomGardenNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomGardenName(event.target.value);
    };
    const handleGardenNameDialogClose = useCallback(() => {
        setShowGardenNameDialog(false); // Close the dialog
        const wasEditing = isEditingExisting; // Capture before state reset

        // If preserving polygon AND we were NOT editing, redraw saved polygon
        if (preservePolygon && !wasEditing && drawingState.savedPolygonCoordinates.length >= 3) {
              try {
                 const worldPoints = drawingState.savedPolygonCoordinates.map(p => convertToInternalCoords(p));
                 if (worldPoints.length > 0) localOriginRef.current = worldPoints[0].clone(); // Set origin
                 createPolygonFill(worldPoints); // Redraw the final saved state
             } catch(e) {
                 console.error("Error redrawing preserved polygon on name cancel:", e);
                 clearDrawingVisuals(); // Fallback
             }
        } else { // If not preserving, OR if we WERE editing (cancel name = cancel edit result), clear visuals and saved state
            clearDrawingVisuals();
            setDrawingState(prev => ({ ...prev, savedPolygonCoordinates: [] }));
        }

         // Reset naming fields
         setGardenName('');
         setCustomGardenName('');
         setShowCustomNameField(false);
         setIsUncertain(false);
         
         // Reset drawing start time for analytics
         drawingStartTimeRef.current = null;

         // Also reset edit state if naming was part of an edit flow that got cancelled here
         if (wasEditing) {
             setIsEditingExisting(false);
             setGardenBeingEditedName('');
             setGardenBeingEditedUid('');
         }

        if (controls) controls.enabled = true;
    }, [controls, preservePolygon, clearDrawingVisuals, drawingState.savedPolygonCoordinates, createPolygonFill, isEditingExisting]); // Added dependencies

    const handleGardenNameSubmit = useCallback(() => {
        const finalGardenName = (gardenName === 'Custom' ? customGardenName.trim() : gardenName) || undefined;
        const finalUncertainty = isUncertain; // Capture uncertainty
        
        // Track garden submission via analytics
        trackPubGardenSubmission();

        if (onPolygonCompleteRef.current && drawingState.savedPolygonCoordinates.length > 0) {
             // Call the main callback with the *saved* coordinates and the confirmed name
             onPolygonCompleteRef.current(drawingState.savedPolygonCoordinates, finalGardenName, finalUncertainty);
        } else {
            console.warn("Cannot submit garden name: No callback or no saved coordinates.");
        }

        // Close dialog and reset naming fields
        setShowGardenNameDialog(false);
        setGardenName('');
        setCustomGardenName('');
        setShowCustomNameField(false);
        setIsUncertain(false); // Reset uncertainty
        
        // Reset drawing start time after analytics
        drawingStartTimeRef.current = null;

        // Reset edit state just in case (should be false)
        if (isEditingExisting) {
            setIsEditingExisting(false);
            setGardenBeingEditedName('');
            setGardenBeingEditedUid('');
        }

        // Clear drawing visuals (the green polygon) since it will be reloaded as an orange garden by parent
        clearDrawingVisuals();
        setDrawingState(prev => ({ ...prev, savedPolygonCoordinates: [] })); // Clear tool's saved coords

        if (controls) controls.enabled = true;
    }, [gardenName, customGardenName, controls, drawingState.savedPolygonCoordinates, isEditingExisting, clearDrawingVisuals, isUncertain, onPolygonCompleteRef]); // Added dependencies


    // --- Render ---
    return (
        <>
            {/* Drawing Controls Overlay */}
            <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 900, display: 'flex', flexDirection: 'column', gap: 1, pointerEvents: 'none' /* Allow clicks through */ }}>
                {/* Top-level buttons that need pointer events */}
                <Box sx={{ 
                    pointerEvents: 'auto', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1,
                    '& > *': { marginBottom: '4px' } // Add spacing between buttons
                }}>
                    {/* Show Draw/Edit button only when NOT busy and NOT in elevation edit mode */}
                    {!drawingState.isDrawing && !drawingState.isEditingElevation && !isEditingExisting && !showGardenNameDialog && (
                        <Tooltip title={drawingState.savedPolygonCoordinates.length > 0 ? "Start New Drawing" : "Draw Outdoor Space"}>
                            <span> {/* Span needed for disabled button tooltip */}
                                <IconButton
                                    color="primary"
                                    onClick={startDrawing}
                                    sx={{
                                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' },
                                    }}
                                    size="medium"
                                >
                                    <EditIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    {/* Controls shown DURING drawing (either new or editing existing) */}
                    {drawingState.isDrawing && !drawingState.isEditingElevation && (
                        <>
                            <Tooltip title="Finish Drawing">
                                <span>
                                    <IconButton
                                        color="success"
                                        onClick={finishDrawing}
                                        disabled={drawingState.isSettingElevation || drawingState.polygonPoints.length < 3}
                                        sx={{ backgroundColor: 'rgba(67, 160, 71, 0.8)', color: '#fff', '&:hover': { backgroundColor: 'rgba(67, 160, 71, 1)' }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }}
                                        size="medium"
                                    >
                                        <CheckIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton
                                    color="error"
                                    onClick={cancelDrawing}
                                    sx={{ backgroundColor: 'rgba(211, 47, 47, 0.8)', color: '#fff', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 1)' } }}
                                    size="medium"
                                >
                                    <CancelIcon />
                                </IconButton>
                            </Tooltip>
                            {/* Undo/Restart only available AFTER setting elevation */}
                            {!drawingState.isSettingElevation && (
                                <>
                                    <Tooltip title="Undo Last Point">
                                        <span>
                                            <IconButton
                                                color="primary"
                                                onClick={undoLastPoint}
                                                disabled={drawingState.polygonPoints.length === 0}
                                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }}
                                                size="medium"
                                            >
                                                <UndoIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={isEditingExisting ? "Restart Disabled During Edit" : "Restart Polygon Outline"}>
                                        <span>
                                            <IconButton
                                                color="secondary"
                                                onClick={restartDrawing}
                                                disabled={drawingState.polygonPoints.length === 0 || isEditingExisting} // Disable restart if editing existing
                                                sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }, '&.Mui-disabled': { backgroundColor: 'rgba(0, 0, 0, 0.12)' } }}
                                                size="medium"
                                            >
                                                <RestartAltIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )}
                        </>
                    )}
                    {/* Cancel button shown DURING elevation editing */}
                    {drawingState.isEditingElevation && (
                        <Tooltip title="Cancel Elevation Edit">
                            <IconButton
                                color="error"
                                onClick={cancelEditingElevation}
                                sx={{ backgroundColor: 'rgba(211, 47, 47, 0.8)', color: '#fff', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 1)' } }}
                                size="medium"
                            >
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                 </Box>
            </Box>

            {/* Elevation Adjustment Controls (Bottom Center) - Show only if editing elevation */}
            {drawingState.isEditingElevation && (
                <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'auto' }}>
                    <Paper elevation={3} sx={{ display: 'flex', alignItems: 'center', padding: '8px 16px', borderRadius: 3, backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(5px)' }}>
                        <Typography variant="body2" sx={{ mr: 2, fontWeight: 500, minWidth: '80px', textAlign: 'center' }}>
                            Height: {drawingState.elevationAdjustment >= 0 ? '+' : ''}{drawingState.elevationAdjustment.toFixed(1)}m
                        </Typography>
                        {/* Use mouse down/up/leave for press-and-hold */}
                        <IconButton
                             onMouseDown={() => handleElevationButtonPress('down')}
                             onMouseUp={clearAccelerationInterval}
                             onMouseLeave={clearAccelerationInterval}
                             color="primary"
                             size="medium"
                             sx={{ mx: 0.5 }}
                             aria-label="Decrease elevation"
                         >
                             <ArrowDownwardIcon />
                         </IconButton>
                         <IconButton
                             onMouseDown={() => handleElevationButtonPress('up')}
                             onMouseUp={clearAccelerationInterval}
                             onMouseLeave={clearAccelerationInterval}
                             color="primary"
                             size="medium"
                             sx={{ mx: 0.5 }}
                             aria-label="Increase elevation"
                         >
                             <ArrowUpwardIcon />
                         </IconButton>
                        <Button onClick={applyElevationAdjustment} variant="contained" color="primary" size="small" disabled={drawingState.elevationAdjustment === 0} sx={{ ml: 2 }} startIcon={<CheckIcon />}>
                            Apply
                        </Button>
                    </Paper>
                </Box>
            )}

            {/* --- Dialogs --- */}

            {/* Initial Welcome/Help Dialog */}
            <Dialog open={showWelcomeDialog} onClose={() => setShowWelcomeDialog(false)} PaperProps={{ sx: { borderRadius: 3, maxWidth: '450px' } }}>
                <DialogTitle>How to Draw the Garden Area</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        <b>Navigate:</b> Swipe to pan around, pinch to zoom in and out.
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        <b>Draw:</b> Tap the '+' button to start. To place points, use quick, short taps (not long presses or drags).
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        <b>Edit:</b> Tap any orange garden area to select it for editing or adjusting height.
                    </Typography>
                </DialogContent>
                <DialogActions><Button onClick={() => setShowWelcomeDialog(false)} color="primary">Got it</Button></DialogActions>
            </Dialog>

            {/* Drawing Instructions Dialog (for new drawing OR tracing outline during edit) */}
            <Dialog
                open={showDrawingDialog}
                onClose={() => setShowDrawingDialog(false)} // Backdrop click ONLY hides dialog
                PaperProps={{ sx: { maxWidth: '400px', pointerEvents: 'auto', borderRadius: 2 } }}
                aria-labelledby="drawing-instructions-title"
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} id="drawing-instructions-title">
                    {isEditingExisting ? `Editing: ${gardenBeingEditedName}` : 'Drawing Instructions'}
                    <IconButton size="small" onClick={() => setShowDrawingDialog(false)} aria-label="close"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, paddingBottom: 3 }}>
                    <GrassIcon color="primary" />
                    <Typography>{drawingState.drawingPrompt}</Typography>
                </DialogContent>
            </Dialog>

             {/* Edit Garden Instructions Dialog (Shown once when edit starts) */}
            <Dialog open={showEditGardenDialog} onClose={() => setShowEditGardenDialog(false)} PaperProps={{ sx: { borderRadius: 2, maxWidth: '450px' } }} aria-labelledby="edit-garden-title">
                <DialogTitle id="edit-garden-title">Edit Garden Outline</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        The current garden is shown in orange.
                    </Typography>
                    <Typography>
                        You'll now draw a new outline for <strong>'{gardenBeingEditedName}'</strong>.
                    </Typography>
                     <Typography sx={{mt: 1}}>
                       Start by tapping the ground to set the height for your new shape.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDrawing} color="inherit">Cancel</Button>
                    <Button onClick={() => setShowEditGardenDialog(false)} color="primary" autoFocus>Start Drawing</Button>
                </DialogActions>
            </Dialog>


            {/* Elevation Editing Instructions Dialog */}
            <Dialog open={showElevationDialog} onClose={dismissElevationDialog} PaperProps={{ sx: { maxWidth: '400px', borderRadius: 2 } }} aria-labelledby="elevation-instructions-title">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} id="elevation-instructions-title">
                    Adjust Height: {gardenBeingEditedName}
                    <IconButton size="small" onClick={dismissElevationDialog} aria-label="close"><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, paddingBottom: 2 }}>
                    <HeightIcon color="primary" />
                    <Typography variant="body2">
                        Use the up/down arrows to adjust the height of the green shape. Tap and hold for faster adjustments. Tap 'Apply' when done.
                    </Typography>
                </DialogContent>
                 <DialogActions>
                    {/* Cancel button directly calls cancelEditingElevation */}
                    <Button onClick={cancelEditingElevation} color="inherit">Cancel</Button>
                    <Button onClick={dismissElevationDialog} color="primary">OK</Button>
                </DialogActions>
            </Dialog>

            {/* Close Polygon Confirmation Dialog */}
            <Dialog open={closePolygonDialog} onClose={() => setClosePolygonDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle>Finish Drawing?</DialogTitle>
                <DialogContent><Typography>Complete the garden outline and finish?</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setClosePolygonDialog(false)}>Keep Drawing</Button>
                    <Button onClick={finishDrawing} color="primary" autoFocus>Finish</Button>
                </DialogActions>
            </Dialog>

             {/* Start New Drawing Confirmation Dialog */}
            <Dialog open={showStartNewDrawingDialog} onClose={() => setShowStartNewDrawingDialog(false)} PaperProps={{ sx: { borderRadius: 2 } }}>
                <DialogTitle>Start New Drawing?</DialogTitle>
                <DialogContent><Typography>Clear the current drawing and start a new one?</Typography></DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowStartNewDrawingDialog(false)}>Cancel</Button>
                    <Button onClick={() => { setShowStartNewDrawingDialog(false); startNewDrawing(); }} color="primary" autoFocus>Start New</Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Edit Dialog */}
            <Dialog
                open={showConfirmEditDialog}
                onClose={() => setShowConfirmEditDialog(false)} // Simple close on backdrop
                PaperProps={{ sx: { borderRadius: 2 } }}
                aria-labelledby="confirm-edit-title"
            >
                <DialogTitle id="confirm-edit-title">Confirm New Outline</DialogTitle>
                <DialogContent>
                    <Typography>
                        Applying will overwrite the existing garden '{gardenBeingEditedName}'. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setShowConfirmEditDialog(false);
                        // Clean up after explicit cancelation
                        clearDrawingVisuals();
                        setDrawingState(prev => ({ ...prev, savedPolygonCoordinates: [] }));
                        setIsEditingExisting(false);
                        setGardenBeingEditedName('');
                        setGardenBeingEditedUid('');
                        setTempSavedData(null);
                        setIsUncertain(false);
                        if (controls) controls.enabled = true;
                    }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmEdit} color="warning" autoFocus>
                        Overwrite Garden
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Garden Naming Dialog */}
            {handleGardenNaming && (
                <Dialog
                    open={showGardenNameDialog}
                    onClose={handleGardenNameDialogClose} // Handles cancel logic correctly
                    disablePortal
                    container={container} // Attach to the viewer container if possible
                    PaperProps={{ sx: { borderRadius: 3, maxWidth: '450px', width: '90%', m: 'auto', backgroundColor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(5px)' } }}
                    BackdropProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' } }}
                    keepMounted={false} // Unmount when closed
                    aria-labelledby="garden-name-dialog-title"
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }} id="garden-name-dialog-title"><GrassIcon color="primary" /> Name this Area</DialogTitle>
                    <DialogContent sx={{ pt: '20px !important' }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>Select a name for the area you've drawn.</Typography>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="garden-name-select-label">Garden Name</InputLabel>
                            <Select labelId="garden-name-select-label" value={gardenName} label="Garden Name" onChange={handleGardenNameChange}>
                                <MenuItem value="Front Garden">Front Garden</MenuItem>
                                <MenuItem value="Back Garden">Back Garden</MenuItem>
                                <MenuItem value="Side Garden">Side Garden</MenuItem>
                                <MenuItem value="Rooftop Garden">Rooftop Garden</MenuItem>
                                <MenuItem value="Beer Garden">Beer Garden</MenuItem>
                                <MenuItem value="Patio">Patio</MenuItem>
                                <MenuItem value="Terrace">Terrace</MenuItem>
                                <MenuItem value="Custom">Custom Name...</MenuItem>
                            </Select>
                        </FormControl>
                        {showCustomNameField && (<TextField fullWidth label="Enter Custom Name" variant="outlined" value={customGardenName} onChange={handleCustomGardenNameChange} />)}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isUncertain}
                                    onChange={(e) => setIsUncertain(e.target.checked)}
                                    sx={{ color: '#FF8C00', '&.Mui-checked': { color: '#FF8C00' } }}
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <HelpOutlineIcon sx={{ mr: 1, color: '#FF8C00' }} />
                                    <Typography variant="body2">I'm uncertain about this garden's boundaries</Typography>
                                </Box>
                            }
                            sx={{ mt: 2 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ padding: '16px 24px' }}>
                        <Button onClick={handleGardenNameDialogClose} >Cancel</Button>
                        <Tooltip title={(!gardenName || (gardenName === 'Custom' && !customGardenName.trim())) ? "Please select or enter a name" : ""}>
                            <span>
                                <Button onClick={handleGardenNameSubmit} variant="contained" color="primary" disabled={!gardenName || (gardenName === 'Custom' && !customGardenName.trim())}>Confirm</Button>
                            </span>
                        </Tooltip>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
});

export default GardenDrawingTool;